import { useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, type UseQueryResult } from '@tanstack/react-query';
import { ETIQUETA_CATALOGO, type CodigoCatalogo, type Pagina, type TipoAcceso } from '@cmms/shared';
import { api, consulta } from '../servicios/api.js';
import { CasillaVerificacion } from './piezas.js';
import { IconoLupa } from './iconos.js';

/**
 * Orígenes de opciones para los formularios de alta y edición.
 *
 * Todas las pantallas piden las mismas listas —ubicaciones, activos, catálogos,
 * usuarios, grupos—, así que se resuelven en un solo lugar y comparten la caché
 * de React Query: abrir el formulario de una orden después del de un activo no
 * vuelve a pedir el árbol de ubicaciones.
 */

export interface Opcion {
  id: string;
  nombre: string;
}

export interface NodoUbicacion extends Opcion {
  padreId: string | null;
}

export interface ItemCatalogo extends Opcion {
  descripcion: string | null;
  padreId: string | null;
  padreNombre: string | null;
  activo: boolean;
  enUso: number;
}

export interface TipoActivoOpcion extends Opcion {
  descripcion: string | null;
  activos: number;
  tiposFalla: { id: string; nombre: string }[];
  campos: { campo: string; obligatorio: boolean }[];
}

export interface TipoUbicacionOpcion extends Opcion {
  descripcion: string | null;
  ubicaciones: number;
  /** Ítems de texto que se copian al checklist de cada ubicación nueva de este tipo. */
  checklistBase: { id: string; texto: string; orden: number }[];
}

export interface UsuarioOpcion extends Opcion {
  tipoAcceso: TipoAcceso;
  activo: boolean;
}

export interface GrupoOpcion extends Opcion {
  tipo: string;
  miembros: number;
  miembrosDetalle: { id: string; nombre: string }[];
}

export interface TipoFallaOpcion extends Opcion {
  familia: string | null;
  activo: boolean;
}

// ─── Consultas ───────────────────────────────────────────────────────────────

export function useUbicaciones(habilitada = true): UseQueryResult<NodoUbicacion[]> {
  return useQuery({
    queryKey: ['ubicaciones-arbol'],
    queryFn: () => api.get<NodoUbicacion[]>('/ubicaciones/arbol'),
    enabled: habilitada,
  });
}

export function useCatalogo(
  codigo: CodigoCatalogo,
  opciones: { padreId?: string; habilitada?: boolean } = {},
): UseQueryResult<ItemCatalogo[]> {
  const { padreId, habilitada = true } = opciones;
  return useQuery({
    queryKey: ['catalogo', codigo, padreId ?? ''],
    queryFn: () => api.get<ItemCatalogo[]>(`/catalogos/${codigo}${consulta({ padreId })}`),
    enabled: habilitada,
  });
}

export function useTiposActivo(habilitada = true): UseQueryResult<TipoActivoOpcion[]> {
  return useQuery({
    queryKey: ['tipos-activo'],
    queryFn: () => api.get<TipoActivoOpcion[]>('/tipos-activo'),
    enabled: habilitada,
  });
}

export function useTiposUbicacion(habilitada = true): UseQueryResult<TipoUbicacionOpcion[]> {
  return useQuery({
    queryKey: ['tipos-ubicacion'],
    queryFn: () => api.get<TipoUbicacionOpcion[]>('/tipos-ubicacion'),
    enabled: habilitada,
  });
}

export function useTiposFalla(habilitada = true): UseQueryResult<TipoFallaOpcion[]> {
  return useQuery({
    queryKey: ['tipos-falla'],
    queryFn: () => api.get<TipoFallaOpcion[]>('/tipos-falla'),
    enabled: habilitada,
  });
}

/**
 * Fallas típicas del tipo de activo (§9.4b) — para el desplegable al escanear
 * un QR. Ruta propia sin `tipo_falla:leer` (`/tipos-falla/de-activo/:id`, ver
 * el controller): la usa cualquiera que pueda reportar una solicitud,
 * incluido un Solicitante, que nunca tiene ese permiso de catálogo.
 */
export function useTiposFallaDeActivo(
  tipoActivoId: string,
  /** Prefijo de la ruta cuando el formulario corre sin sesión (`/publico/qr/:codigo`). */
  rutaBase?: string,
): UseQueryResult<TipoFallaOpcion[]> {
  return useQuery({
    queryKey: ['tipos-falla', 'de-tipo-activo', rutaBase ?? '', tipoActivoId],
    queryFn: () => {
      if (!rutaBase) return api.get<TipoFallaOpcion[]>(`/tipos-falla/de-activo/${tipoActivoId}`);
      // `rutaBase` puede traer query (`?t=`): la ruta va antes y la query después.
      const [base, query] = rutaBase.split('?');
      return api.get<TipoFallaOpcion[]>(`${base}/tipos-falla/${tipoActivoId}${query ? `?${query}` : ''}`);
    },
    enabled: tipoActivoId !== '',
  });
}

/** Padrón completo para asignar: la vista paginada no sirve como origen de opciones. */
export function useUsuariosOpciones(habilitada = true): UseQueryResult<UsuarioOpcion[]> {
  return useQuery({
    queryKey: ['usuarios-opciones'],
    queryFn: async () => {
      const pagina = await api.get<Pagina<{ id: string; nombreCompleto: string; tipoAcceso: TipoAcceso; activo: boolean }>>(
        '/usuarios?tamano=100',
      );
      return pagina.datos
        .filter((u) => u.activo)
        .map((u) => ({ id: u.id, nombre: u.nombreCompleto, tipoAcceso: u.tipoAcceso, activo: u.activo }));
    },
    enabled: habilitada,
  });
}

export function useGruposOpciones(habilitada = true): UseQueryResult<GrupoOpcion[]> {
  return useQuery({
    queryKey: ['grupos'],
    queryFn: async () => {
      const grupos = await api.get<
        { id: string; nombre: string; tipo: string; miembros: { id: string; nombreCompleto: string }[] }[]
      >('/grupos');
      return grupos.map((g) => ({
        id: g.id,
        nombre: g.nombre,
        tipo: g.tipo,
        miembros: g.miembros.length,
        miembrosDetalle: g.miembros.map((m) => ({ id: m.id, nombre: m.nombreCompleto })),
      }));
    },
    enabled: habilitada,
  });
}

// ─── Selectores ──────────────────────────────────────────────────────────────

/** Cierra un panel flotante al hacer clic o Escape fuera de él. */
function useCerrarAlExterior(abierto: boolean, alCerrar: () => void): React.RefObject<HTMLDivElement> {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!abierto) return;
    const alHacerClic = (e: MouseEvent): void => {
      const objetivo = e.target as HTMLElement;
      // El panel vive en un portal a `document.body` (`PanelFlotante`, para
      // no quedar recortado por el `overflow` de un diálogo): en el DOM real
      // no es descendiente de `ref`, así que sin este chequeo aparte, todo
      // clic ahí adentro se lee como «afuera» y cierra el combo en
      // `mousedown` — antes de que el `click` de la opción llegue a
      // seleccionar nada.
      if (ref.current && !ref.current.contains(objetivo) && !objetivo.closest('.combo-panel')) alCerrar();
    };
    const alTeclear = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') alCerrar();
    };
    document.addEventListener('mousedown', alHacerClic);
    document.addEventListener('keydown', alTeclear);
    return () => {
      document.removeEventListener('mousedown', alHacerClic);
      document.removeEventListener('keydown', alTeclear);
    };
  }, [abierto, alCerrar]);

  return ref;
}

/**
 * Panel flotante de un combo, en un portal a `document.body`.
 *
 * Dentro de un `Dialogo` (que se desplaza con `overflow-y: auto` para admitir
 * formularios largos, §componentes/piezas), un panel `position: absolute`
 * anclado al combo queda recortado por ese mismo scroll en vez de flotar por
 * encima — el combo se «rompe» visualmente contra el borde redondeado del
 * diálogo. El portal escapa de ese recorte; la posición se recalcula a mano
 * porque, fuera del flujo normal, ya no hay `top: 100%` que la resuelva sola.
 */
function PanelFlotante({
  ancla,
  ancho,
  alturaMaxima = 260,
  className,
  role,
  children,
}: {
  ancla: RefObject<HTMLElement>;
  /** Ancho fijo (p. ej. el calendario); por omisión, el mismo ancho que el ancla. */
  ancho?: number;
  /** Tope de alto en px: 260 para los listados; el calendario no lleva tope y solo se acota al espacio de pantalla. */
  alturaMaxima?: number;
  className?: string;
  role?: string;
  children: ReactNode;
}): JSX.Element | null {
  const [posicion, setPosicion] = useState<{ left: number; top: number; width: number; maxHeight: number } | null>(
    null,
  );

  useLayoutEffect(() => {
    const actualizar = (): void => {
      const el = ancla.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const top = r.bottom + 4;
      // Sin este tope, un ancla cerca del borde inferior (p. ej. dentro de un
      // diálogo largo en móvil) dejaba el panel abriéndose hacia abajo del
      // viewport: el `max-height` de `.combo-panel` (260px) no ayudaba porque
      // esto es `position: fixed`, no relativo al `overflow` del diálogo — la
      // parte que sobraba quedaba fuera de pantalla, inalcanzable. Al acotar
      // el alto disponible, el propio scroll interno del panel se hace cargo.
      const maxHeight = Math.max(120, Math.min(alturaMaxima, window.innerHeight - top - 12));
      setPosicion({ left: r.left, top, width: ancho ?? r.width, maxHeight });
    };
    actualizar();
    // `true` (fase de captura): un contenedor interno con su propio scroll
    // (p. ej. el diálogo) dispara el evento ahí, no en window.
    window.addEventListener('scroll', actualizar, true);
    window.addEventListener('resize', actualizar);
    return () => {
      window.removeEventListener('scroll', actualizar, true);
      window.removeEventListener('resize', actualizar);
    };
  }, [ancla, ancho]);

  if (!posicion) return null;

  return createPortal(
    <div
      className={className}
      role={role}
      style={{
        position: 'fixed',
        left: posicion.left,
        top: posicion.top,
        width: posicion.width,
        maxHeight: posicion.maxHeight,
        zIndex: 60,
      }}
    >
      {children}
    </div>,
    document.body,
  );
}

/**
 * Selector de ubicación como árbol desplegable y colapsable.
 *
 * Una sede se expande para mostrar sus sub-ubicaciones; sin eso, una lista
 * plana de «Consultorio 3» repetido en quince sedes es inservible (§4.2). La
 * sede en sí también se puede elegir: hay activos y OTs que viven en la sede
 * y no en una sub-ubicación puntual.
 */
/**
 * Una rama del árbol de ubicaciones, para el selector de un solo valor — se
 * llama a sí misma para los nietos, bisnietos, etc. Antes el árbol solo
 * armaba raíz + un nivel de hijos (`hijosPorPadre.get(raiz.id)`, sin
 * recursión): una ubicación de 3+ niveles ni aparecía como elegible
 * (§ bug reportado — el backend nunca tuvo ese límite, era solo acá).
 */
function FilaArbolUbicacion({
  nodo,
  hijosPorPadre,
  nivel,
  valor,
  expandidos,
  alternar,
  elegir,
}: {
  nodo: NodoUbicacion;
  hijosPorPadre: Map<string, NodoUbicacion[]>;
  nivel: number;
  valor: string;
  expandidos: Set<string>;
  alternar: (id: string) => void;
  elegir: (id: string) => void;
}): JSX.Element {
  const hijos = hijosPorPadre.get(nodo.id) ?? [];
  const expandido = expandidos.has(nodo.id);
  return (
    <div>
      <div className="combo-fila-raiz" style={{ paddingLeft: 22 * (nivel - 1) }}>
        {hijos.length > 0 ? (
          <button
            type="button"
            className="combo-chevron"
            onClick={() => alternar(nodo.id)}
            aria-label={expandido ? `Contraer ${nodo.nombre}` : `Expandir ${nodo.nombre}`}
          >
            {expandido ? '▾' : '▸'}
          </button>
        ) : (
          <span className="combo-chevron-espacio" aria-hidden="true" />
        )}
        <button
          type="button"
          className={`combo-opcion${nivel === 1 ? ' combo-opcion-raiz' : ' combo-opcion-hijo'}${nodo.id === valor ? ' seleccionada' : ''}`}
          onClick={() => elegir(nodo.id)}
        >
          {nodo.nombre}
        </button>
      </div>
      {expandido
        ? hijos.map((hijo) => (
            <FilaArbolUbicacion
              key={hijo.id}
              nodo={hijo}
              hijosPorPadre={hijosPorPadre}
              nivel={nivel + 1}
              valor={valor}
              expandidos={expandidos}
              alternar={alternar}
              elegir={elegir}
            />
          ))
        : null}
    </div>
  );
}

export function SelectorUbicacion({
  id,
  etiqueta = 'Ubicación',
  valor,
  alCambiar,
  requerido = false,
  excluir,
  ubicaciones,
  buscador: buscadorForzado,
}: {
  id: string;
  etiqueta?: string;
  valor: string;
  alCambiar: (valor: string) => void;
  requerido?: boolean;
  /** Impide elegirse a sí misma como padre al editar (§4.3). */
  excluir?: string;
  ubicaciones: NodoUbicacion[] | undefined;
  /** Con término, muestra las coincidencias en lista plana con su ruta completa, en vez del árbol. */
  buscador?: boolean;
}): JSX.Element {
  const [abierto, setAbierto] = useState(false);
  const [expandidos, setExpandidos] = useState<Set<string>>(new Set());
  const [buscar, setBuscar] = useState('');
  const ref = useCerrarAlExterior(abierto, () => setAbierto(false));

  const lista = (ubicaciones ?? []).filter((u) => u.id !== excluir);
  const raices = lista.filter((u) => !u.padreId);
  const hijosPorPadre = new Map<string, NodoUbicacion[]>();
  for (const u of lista) {
    if (u.padreId) hijosPorPadre.set(u.padreId, [...(hijosPorPadre.get(u.padreId) ?? []), u]);
  }
  const seleccionada = lista.find((u) => u.id === valor);
  const buscador = buscadorForzado ?? true;
  const coincidencias = buscador && buscar.trim() !== '' ? coincidenciasUbicacion(lista, buscar) : null;

  function alternar(idNodo: string): void {
    setExpandidos((prev) => {
      const copia = new Set(prev);
      if (copia.has(idNodo)) copia.delete(idNodo);
      else copia.add(idNodo);
      return copia;
    });
  }

  function elegir(idNodo: string): void {
    alCambiar(idNodo);
    setBuscar('');
    setAbierto(false);
  }

  return (
    <div className="campo combo" ref={ref}>
      <label htmlFor={id}>{etiqueta}</label>
      <button
        type="button"
        id={id}
        className="combo-disparador"
        onClick={() => setAbierto((v) => !v)}
        aria-expanded={abierto}
        aria-haspopup="listbox"
      >
        <span>{seleccionada ? seleccionada.nombre : requerido ? 'Seleccione…' : 'Sin ubicación'}</span>
        <span aria-hidden="true">▾</span>
      </button>

      {abierto ? (
        <PanelFlotante ancla={ref} className="combo-panel" role="listbox">
          {buscador ? <BuscadorDesplegable etiqueta={etiqueta} valor={buscar} alCambiar={setBuscar} /> : null}
          {!requerido && buscar.trim() === '' ? (
            <button type="button" className="combo-opcion" onClick={() => elegir('')}>
              Sin ubicación
            </button>
          ) : null}
          {coincidencias ? (
            coincidencias.length === 0 ? (
              <div className="combo-vacio">Ninguna ubicación coincide con la búsqueda.</div>
            ) : (
              coincidencias.map(({ nodo, ruta }) => (
                <button
                  key={nodo.id}
                  type="button"
                  className={`combo-opcion${nodo.id === valor ? ' seleccionada' : ''}`}
                  onClick={() => elegir(nodo.id)}
                >
                  {ruta}
                </button>
              ))
            )
          ) : (
            <>
              {raices.length === 0 ? <div className="combo-vacio">No hay ubicaciones creadas.</div> : null}
              {raices.map((raiz) => (
                <FilaArbolUbicacion
                  key={raiz.id}
                  nodo={raiz}
                  hijosPorPadre={hijosPorPadre}
                  nivel={1}
                  valor={valor}
                  expandidos={expandidos}
                  alternar={alternar}
                  elegir={elegir}
                />
              ))}
            </>
          )}
        </PanelFlotante>
      ) : null}
    </div>
  );
}

/**
 * Selector de activo como combobox: se escribe para filtrar y las coincidencias
 * aparecen debajo, en el mismo control, en vez de un buscador y una lista
 * aparte que obligan a mirar dos sitios para una sola elección.
 *
 * El listado va acotado por la API y no por el navegador: con miles de equipos,
 * traerlos todos para filtrarlos en el cliente es lo que vuelve inusable el
 * formulario justo en la sede más grande.
 */
export function SelectorActivo({
  id,
  etiqueta = 'Activo',
  valor,
  alCambiar,
  ubicacionId,
  nombreActual,
  excluir,
}: {
  id: string;
  etiqueta?: string;
  valor: string;
  alCambiar: (valor: string) => void;
  ubicacionId?: string;
  /** Nombre del activo ya seleccionado, por si queda fuera de la búsqueda en curso. */
  nombreActual?: string | null;
  /** Impide que un activo se elija a sí mismo como padre y se cierre el ciclo. */
  excluir?: string;
}): JSX.Element {
  const [abierto, setAbierto] = useState(false);
  const [buscar, setBuscar] = useState('');
  const [nombreElegido, setNombreElegido] = useState<string | null>(nombreActual ?? null);
  const ref = useCerrarAlExterior(abierto, () => setAbierto(false));

  // El padre puede resetear la selección (p. ej. al cambiar la ubicación del
  // formulario) sin pasar por `elegir`; sin este efecto el combo seguiría
  // mostrando el nombre del activo anterior.
  useEffect(() => {
    if (valor === '') setNombreElegido(null);
  }, [valor]);

  // Un activo llegado por id (p. ej. al crear la OT desde una observación) no
  // trae su nombre: se pide para no mostrar el campo vacío con el activo ya
  // elegido por debajo.
  const { data: activoDelValor } = useQuery({
    queryKey: ['activo-nombre', valor],
    queryFn: () => api.get<{ nombre: string }>(`/activos/${valor}`),
    enabled: valor !== '' && !nombreElegido,
  });

  const { data } = useQuery({
    queryKey: ['activos-opciones', ubicacionId ?? '', buscar],
    queryFn: () =>
      api.get<
        Pagina<{
          id: string;
          nombre: string;
          ubicacionNombre: string | null;
          tiposActivo: { id: string; nombre: string }[];
        }>
      >(`/activos${consulta({ tamano: 25, orden: 'nombre', ubicacionId, buscar })}`),
    enabled: abierto,
  });

  // Orden de conveniencia, no un filtro (§ pedido): "Personal" sigue
  // eligible acá, solo se corre al final para no estorbar arriba de la lista
  // en el alta de una OT.
  const esPersonal = (a: { tiposActivo: { nombre: string }[] }): boolean =>
    a.tiposActivo.some((t) => t.nombre.trim().toLowerCase() === 'personal');
  const opciones = (data?.datos ?? [])
    .filter((a) => a.id !== excluir)
    .sort((a, b) => Number(esPersonal(a)) - Number(esPersonal(b)));

  function elegir(activo: { id: string; nombre: string }): void {
    alCambiar(activo.id);
    setNombreElegido(activo.nombre);
    setBuscar('');
    setAbierto(false);
  }

  function limpiar(): void {
    alCambiar('');
    setNombreElegido(null);
    setBuscar('');
    setAbierto(false);
  }

  return (
    <div className="campo combo" ref={ref}>
      <label htmlFor={id}>{etiqueta}</label>
      <input
        id={id}
        type="text"
        autoComplete="off"
        value={abierto ? buscar : (nombreElegido ?? activoDelValor?.nombre ?? '')}
        onFocus={() => setBuscar('')}
        onClick={() => setAbierto(true)}
        onChange={(e) => {
          setAbierto(true);
          setBuscar(e.target.value);
        }}
        placeholder="Filtrar activos por nombre o n.º de serie…"
      />

      {abierto ? (
        <PanelFlotante ancla={ref} className="combo-panel" role="listbox">
          <button type="button" className="combo-opcion" onClick={limpiar}>
            Sin activo
          </button>
          {opciones.map((a) => (
            <button key={a.id} type="button" className="combo-opcion" onClick={() => elegir(a)}>
              {a.nombre}
              {a.ubicacionNombre ? ` · ${a.ubicacionNombre}` : ''}
            </button>
          ))}
          {opciones.length === 0 ? <div className="combo-vacio">Sin resultados.</div> : null}
          {data && data.total > data.datos.length ? (
            <div className="combo-vacio">
              Se muestran {data.datos.length} de {data.total}. Acote la búsqueda.
            </div>
          ) : null}
        </PanelFlotante>
      ) : null}
    </div>
  );
}

export interface ParteBuscable {
  id: string;
  nombre: string;
  idPieza: string;
  costoUnitarioPen: string;
  stock: { ubicacionId: string; ubicacionNombre: string; unidadesEnExistencia: number }[];
}

/**
 * Selector de parte (repuesto) como combobox buscable, igual que `SelectorActivo`:
 * reemplaza al input de búsqueda + `<select>` nativo de "Partes consumidas"
 * (§ pedido), que quedaba fuera de estilo con el resto del formulario. Entrega
 * la parte completa (no solo el id) porque quien la usa necesita su stock por
 * ubicación para el selector de "Tomar de".
 */
export function SelectorParte({
  id,
  etiqueta = 'Parte',
  valor,
  alElegir,
}: {
  id: string;
  etiqueta?: string;
  valor: string;
  alElegir: (parte: ParteBuscable | null) => void;
}): JSX.Element {
  const [abierto, setAbierto] = useState(false);
  const [buscar, setBuscar] = useState('');
  const [nombreElegido, setNombreElegido] = useState<string | null>(null);
  const ref = useCerrarAlExterior(abierto, () => setAbierto(false));

  useEffect(() => {
    if (valor === '') setNombreElegido(null);
  }, [valor]);

  const { data } = useQuery({
    queryKey: ['partes-opciones', buscar],
    queryFn: () => api.get<Pagina<ParteBuscable>>(`/partes${consulta({ tamano: 50, buscar })}`),
    enabled: abierto,
  });

  const opciones = data?.datos ?? [];

  function elegir(parte: ParteBuscable): void {
    alElegir(parte);
    setNombreElegido(parte.nombre);
    setBuscar('');
    setAbierto(false);
  }

  function limpiar(): void {
    alElegir(null);
    setNombreElegido(null);
    setBuscar('');
    setAbierto(false);
  }

  return (
    <div className="campo combo" ref={ref}>
      <label htmlFor={id}>{etiqueta}</label>
      <input
        id={id}
        type="text"
        autoComplete="off"
        value={abierto ? buscar : (nombreElegido ?? '')}
        onFocus={() => setBuscar('')}
        onClick={() => setAbierto(true)}
        onChange={(e) => {
          setAbierto(true);
          setBuscar(e.target.value);
        }}
        placeholder="Nombre o ID de pieza…"
      />

      {abierto ? (
        <PanelFlotante ancla={ref} className="combo-panel" role="listbox">
          {valor !== '' ? (
            <button type="button" className="combo-opcion" onClick={limpiar}>
              Sin parte
            </button>
          ) : null}
          {opciones.map((p) => {
            const existencias = p.stock.reduce((n, s) => n + s.unidadesEnExistencia, 0);
            return (
              <button key={p.id} type="button" className="combo-opcion" onClick={() => elegir(p)}>
                <span>{p.nombre}</span>
                <span className="campo-nota" style={{ marginLeft: 8 }}>
                  ID {p.idPieza} · {existencias} disp. en total
                </span>
              </button>
            );
          })}
          {opciones.length === 0 ? <div className="combo-vacio">Sin resultados.</div> : null}
          {data && data.total > data.datos.length ? (
            <div className="combo-vacio">
              Se muestran {data.datos.length} de {data.total}. Acote la búsqueda.
            </div>
          ) : null}
        </PanelFlotante>
      ) : null}
    </div>
  );
}

/**
 * Selector de persona (sujeto evaluable) como combobox, igual que `SelectorActivo`:
 * se escribe para filtrar por nombre y el padre puede acotar por ubicación. El
 * listado va acotado por la API para no traer todo el personal de una vez.
 */
export function SelectorPersona({
  id,
  etiqueta = 'Persona',
  valor,
  alCambiar,
  ubicacionId,
  nombreActual,
}: {
  id: string;
  etiqueta?: string;
  valor: string;
  alCambiar: (valor: string) => void;
  /** Acota el listado a esta ubicación y sus sub-ubicaciones. */
  ubicacionId?: string;
  /** Nombre de la persona ya elegida, por si queda fuera de la búsqueda en curso. */
  nombreActual?: string | null;
}): JSX.Element {
  const [abierto, setAbierto] = useState(false);
  const [buscar, setBuscar] = useState('');
  const [nombreElegido, setNombreElegido] = useState<string | null>(nombreActual ?? null);
  const ref = useCerrarAlExterior(abierto, () => setAbierto(false));

  useEffect(() => {
    if (valor === '') setNombreElegido(null);
  }, [valor]);

  // Una persona llegada por id (p. ej. al editar la OT) no trae su nombre aquí.
  const { data: personaDelValor } = useQuery({
    queryKey: ['persona-nombre', valor],
    queryFn: () => api.get<{ nombreCompleto: string }>(`/sujetos-evaluables/${valor}`),
    enabled: valor !== '' && !nombreElegido,
  });

  const { data } = useQuery({
    queryKey: ['personas-opciones', ubicacionId ?? '', buscar],
    queryFn: () =>
      api.get<
        Pagina<{
          id: string;
          nombreCompleto: string;
          rol: string;
          ubicacion: string | null;
        }>
      >(`/sujetos-evaluables${consulta({ pagina: 1, tamano: 25, activo: 'true', ubicacionId, buscar })}`),
    enabled: abierto,
  });

  function elegir(persona: { id: string; nombreCompleto: string }): void {
    alCambiar(persona.id);
    setNombreElegido(persona.nombreCompleto);
    setBuscar('');
    setAbierto(false);
  }

  return (
    <div className="campo combo" ref={ref}>
      <label htmlFor={id}>{etiqueta}</label>
      <input
        id={id}
        type="text"
        autoComplete="off"
        value={abierto ? buscar : (nombreElegido ?? personaDelValor?.nombreCompleto ?? '')}
        onFocus={() => setBuscar('')}
        onClick={() => setAbierto(true)}
        onChange={(e) => {
          setAbierto(true);
          setBuscar(e.target.value);
        }}
        placeholder="Filtrar personas por nombre…"
      />

      {abierto ? (
        <PanelFlotante ancla={ref} className="combo-panel" role="listbox">
          {(data?.datos ?? []).map((p) => (
            <button key={p.id} type="button" className="combo-opcion" onClick={() => elegir(p)}>
              {p.nombreCompleto}
              {` · ${p.rol}`}
              {p.ubicacion ? ` (${p.ubicacion})` : ''}
            </button>
          ))}
          {data && data.datos.length === 0 ? <div className="combo-vacio">Sin resultados.</div> : null}
          {data && data.total > data.datos.length ? (
            <div className="combo-vacio">
              Se muestran {data.datos.length} de {data.total}. Acote la búsqueda.
            </div>
          ) : null}
        </PanelFlotante>
      ) : null}
    </div>
  );
}

/** Desplegable de un catálogo administrable (§3.2). */
export function SelectorCatalogo({
  id,
  codigo,
  etiqueta,
  valor,
  alCambiar,
  padreId,
  requerido = false,
  habilitado = true,
  textoVacio,
  alCrear,
  textoCrear = '+ Nuevo',
  buscador: buscadorForzado,
}: {
  id: string;
  codigo: CodigoCatalogo;
  etiqueta?: string;
  valor: string;
  alCambiar: (valor: string) => void;
  padreId?: string;
  requerido?: boolean;
  habilitado?: boolean;
  textoVacio?: string;
  /** Si se indica, el panel ofrece crear un valor nuevo desde el mismo selector. */
  alCrear?: () => void;
  textoCrear?: string;
  /** Muestra un buscador arriba del panel para filtrar las opciones por nombre. */
  buscador?: boolean;
}): JSX.Element {
  const { data } = useCatalogo(codigo, { padreId, habilitada: habilitado });
  const items = (data ?? []).filter((i) => i.activo || i.id === valor);
  const [abierto, setAbierto] = useState(false);
  const [buscar, setBuscar] = useState('');
  const ref = useCerrarAlExterior(abierto, () => setAbierto(false));

  const vacioTexto = textoVacio ?? (requerido ? 'Seleccione…' : 'Sin definir');
  const seleccionado = items.find((i) => i.id === valor);
  const buscador = buscadorForzado ?? true;
  const visibles = (buscador ? filtrarOpciones(items, buscar) : items) ?? [];

  function elegir(idNodo: string): void {
    alCambiar(idNodo);
    setBuscar('');
    setAbierto(false);
  }

  return (
    <div className="campo combo" ref={ref}>
      <label htmlFor={id}>{etiqueta ?? ETIQUETA_CATALOGO[codigo]}</label>
      <button
        type="button"
        id={id}
        className="combo-disparador"
        onClick={() => habilitado && setAbierto((v) => !v)}
        aria-expanded={abierto}
        aria-haspopup="listbox"
        disabled={!habilitado}
      >
        <span>{seleccionado ? seleccionado.nombre : vacioTexto}</span>
        <span aria-hidden="true">▾</span>
      </button>

      {abierto ? (
        <PanelFlotante ancla={ref} className="combo-panel" role="listbox">
          {buscador ? <BuscadorDesplegable etiqueta={etiqueta ?? ETIQUETA_CATALOGO[codigo]} valor={buscar} alCambiar={setBuscar} /> : null}
          {!requerido && buscar.trim() === '' ? (
            <button type="button" className={`combo-opcion${valor === '' ? ' seleccionada' : ''}`} onClick={() => elegir('')}>
              {vacioTexto}
            </button>
          ) : null}
          {visibles.map((i) => (
            <button
              key={i.id}
              type="button"
              className={`combo-opcion${i.id === valor ? ' seleccionada' : ''}`}
              onClick={() => elegir(i.id)}
            >
              {i.nombre}
            </button>
          ))}
          {items.length === 0 ? <div className="combo-vacio">Sin opciones.</div> : null}
          {items.length > 0 && visibles.length === 0 ? <div className="combo-vacio">Ninguna opción coincide con la búsqueda.</div> : null}
          {alCrear ? (
            <button
              type="button"
              className="combo-opcion"
              onClick={() => {
                setAbierto(false);
                alCrear();
              }}
            >
              {textoCrear}
            </button>
          ) : null}
        </PanelFlotante>
      ) : null}

      {habilitado && data && data.length === 0 ? (
        <span className="campo-nota">
          El catálogo «{ETIQUETA_CATALOGO[codigo]}» está vacío. Se administra en Catálogos.
        </span>
      ) : null}
    </div>
  );
}

/**
 * Grupo de botones ("pastillas") para un puñado de opciones fijas — más
 * rápido de leer y de tocar que un desplegable cuando son 3 o 4 valores
 * conocidos de memoria (prioridad, por ejemplo). No pensado para listas
 * largas o cargadas por API: para eso sigue siendo mejor `SelectorLista`.
 */
export function SelectorSegmentado({
  id,
  etiqueta,
  valor,
  alCambiar,
  opciones,
}: {
  id: string;
  etiqueta?: string;
  valor: string;
  alCambiar: (valor: string) => void;
  opciones: Opcion[];
}): JSX.Element {
  return (
    <div className="campo" style={{ marginBottom: 0 }}>
      {etiqueta ? <span className="campo-titulo">{etiqueta}</span> : null}
      <div id={id} className="selector-segmentado" role="radiogroup" aria-label={etiqueta}>
        {opciones.map((o) => (
          <button
            key={o.id}
            type="button"
            role="radio"
            aria-checked={valor === o.id}
            className={valor === o.id ? 'activo' : ''}
            onClick={() => alCambiar(o.id)}
          >
            {o.nombre}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Desplegable propio para reemplazar `<select>` nativo en contextos donde el
 * control nativo del sistema operativo (pantalla completa en móvil) no
 * conviene — p. ej. dentro de un modal de filtros. Admite opciones agrupadas
 * (`grupos`) para el caso de «grupo o usuario» en un mismo campo.
 */
export function SelectorLista({
  id,
  etiqueta,
  valor,
  alCambiar,
  opciones,
  grupos,
  textoVacio = 'Todos',
  vacio,
  requerido = false,
  deshabilitado = false,
  buscador: buscadorForzado,
}: {
  id: string;
  etiqueta?: string;
  valor: string;
  alCambiar: (valor: string) => void;
  opciones?: Opcion[];
  grupos?: { etiqueta: string; opciones: Opcion[] }[];
  textoVacio?: string;
  /** Mensaje cuando no hay opciones (ni sueltas ni agrupadas) que mostrar. */
  vacio?: string;
  /** Campo de valor fijo (p. ej. prioridad): sin opción para vaciarlo. */
  requerido?: boolean;
  deshabilitado?: boolean;
  /** Muestra un buscador arriba del panel para filtrar las opciones por nombre. */
  buscador?: boolean;
}): JSX.Element {
  const [abierto, setAbierto] = useState(false);
  const [buscar, setBuscar] = useState('');
  const ref = useCerrarAlExterior(abierto, () => setAbierto(false));

  const todas = [...(opciones ?? []), ...(grupos ?? []).flatMap((g) => g.opciones)];
  const seleccionada = todas.find((o) => o.id === valor);
  const sinOpciones = todas.length === 0;
  const buscador = buscadorForzado ?? true;
  const filtradas = buscador ? filtrarOpciones(opciones, buscar) : opciones;
  const gruposFiltrados = buscador ? grupos?.map((g) => ({ ...g, opciones: filtrarOpciones(g.opciones, buscar) ?? [] })).filter((g) => g.opciones.length > 0) : grupos;
  const sinCoincidencias = buscador && buscar.trim() !== '' && !filtradas?.length && !gruposFiltrados?.length;

  function elegir(idNodo: string): void {
    alCambiar(idNodo);
    setBuscar('');
    setAbierto(false);
  }

  return (
    <div className="campo combo" ref={ref}>
      {etiqueta ? <label htmlFor={id}>{etiqueta}</label> : null}
      <button
        type="button"
        id={id}
        className="combo-disparador"
        onClick={() => !deshabilitado && setAbierto((v) => !v)}
        aria-expanded={abierto}
        aria-haspopup="listbox"
        disabled={deshabilitado}
      >
        <span>{seleccionada ? seleccionada.nombre : textoVacio}</span>
        <span aria-hidden="true">▾</span>
      </button>

      {abierto ? (
        <PanelFlotante ancla={ref} className="combo-panel" role="listbox">
          {buscador ? <BuscadorDesplegable etiqueta={etiqueta ?? 'opciones'} valor={buscar} alCambiar={setBuscar} /> : null}
          {!requerido && buscar.trim() === '' ? (
            <button type="button" className={`combo-opcion${valor === '' ? ' seleccionada' : ''}`} onClick={() => elegir('')}>
              {textoVacio}
            </button>
          ) : null}
          {filtradas?.map((o) => (
            <button
              key={o.id}
              type="button"
              className={`combo-opcion${o.id === valor ? ' seleccionada' : ''}`}
              onClick={() => elegir(o.id)}
            >
              {o.nombre}
            </button>
          ))}
          {gruposFiltrados?.map((g) => (
            <div key={g.etiqueta}>
              <div className="combo-grupo-titulo">{g.etiqueta}</div>
              {g.opciones.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  className={`combo-opcion${o.id === valor ? ' seleccionada' : ''}`}
                  onClick={() => elegir(o.id)}
                >
                  {o.nombre}
                </button>
              ))}
            </div>
          ))}
          {sinOpciones ? <div className="combo-vacio">{vacio ?? 'Sin opciones.'}</div> : null}
          {sinCoincidencias ? <div className="combo-vacio">Ninguna opción coincide con la búsqueda.</div> : null}
        </PanelFlotante>
      ) : null}
    </div>
  );
}

/**
 * Buscador que va arriba del panel de un desplegable. Todos los selectores que
 * admiten `buscador` lo usan, para que el filtro se vea y se comporte igual en todo el módulo.
 */
export function BuscadorDesplegable({
  etiqueta,
  valor,
  alCambiar,
}: {
  etiqueta: string;
  valor: string;
  alCambiar: (valor: string) => void;
}): JSX.Element {
  return (
    <div className="campo cabecera-lista-buscar" style={{ margin: 6 }}>
      <IconoLupa />
      <input
        aria-label={`Buscar ${etiqueta.toLowerCase()}`}
        placeholder="Buscar…"
        value={valor}
        autoFocus
        onChange={(e) => alCambiar(e.target.value)}
      />
    </div>
  );
}

/** Nombre sin tildes ni mayúsculas, para que «Ángel» encuentre «angel» y viceversa. */
function normalizarTexto(texto: string): string {
  return texto.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/** Opciones cuyo nombre contiene el término buscado. Sin término, devuelve todas. */
function filtrarOpciones<T extends Opcion>(opciones: T[] | undefined, buscar: string): T[] | undefined {
  const termino = normalizarTexto(buscar.trim());
  if (!opciones || termino === '') return opciones;
  return opciones.filter((o) => normalizarTexto(o.nombre).includes(termino));
}

/**
 * Ubicaciones que coinciden con el término, cada una con su ruta completa («Sede › Piso › Consultorio»):
 * así se distingue «Consultorio 21» de una sede a otra cuando el árbol se aplana.
 */
function coincidenciasUbicacion(lista: NodoUbicacion[], buscar: string): { nodo: NodoUbicacion; ruta: string }[] {
  const porId = new Map(lista.map((u) => [u.id, u]));
  // Un ciclo en ubicacion_padre_id (A padre de B y B padre de A) haría la recursión infinita: se corta al repetir un nodo.
  const rutaDe = (u: NodoUbicacion, visitados = new Set<string>()): string => {
    visitados.add(u.id);
    const padre = u.padreId ? porId.get(u.padreId) : undefined;
    return padre && !visitados.has(padre.id) ? `${rutaDe(padre, visitados)} › ${u.nombre}` : u.nombre;
  };
  const termino = normalizarTexto(buscar.trim());
  return lista
    .filter((u) => normalizarTexto(u.nombre).includes(termino))
    .map((nodo) => ({ nodo, ruta: rutaDe(nodo) }));
}

/** «2 seleccionadas» / «Ana, Juan» / el vacío — mismo criterio en todo desplegable multi. */
function resumenSeleccion(seleccion: string[], todas: Opcion[], textoVacio: string): string {
  if (seleccion.length === 0) return textoVacio;
  if (seleccion.length === 1) return todas.find((o) => o.id === seleccion[0])?.nombre ?? textoVacio;
  return `${seleccion.length} seleccionados`;
}

/**
 * Igual que `SelectorLista`, pero de selección múltiple: cada opción es un
 * checkbox y el panel no se cierra al elegir, para poder marcar varias
 * seguidas — los filtros que antes eran «uno solo a la vez» ahora se suman
 * dentro de sí mismos (§ pedido).
 */
export function SelectorListaMultiple({
  id,
  etiqueta,
  seleccion,
  alCambiar,
  opciones,
  textoVacio = 'Todos',
  deshabilitado = false,
  buscador: buscadorForzado,
}: {
  id: string;
  etiqueta?: string;
  seleccion: string[];
  alCambiar: (valores: string[]) => void;
  opciones: Opcion[];
  textoVacio?: string;
  deshabilitado?: boolean;
  /** Muestra un buscador arriba del panel para filtrar las opciones por nombre. */
  buscador?: boolean;
}): JSX.Element {
  const [abierto, setAbierto] = useState(false);
  const [buscar, setBuscar] = useState('');
  const ref = useCerrarAlExterior(abierto, () => setAbierto(false));

  const buscador = buscadorForzado ?? true;
  const visibles = (buscador ? filtrarOpciones(opciones, buscar) : opciones) ?? [];
  const sinCoincidencias = buscador && buscar.trim() !== '' && visibles.length === 0;

  function alternar(idOpcion: string): void {
    alCambiar(
      seleccion.includes(idOpcion) ? seleccion.filter((v) => v !== idOpcion) : [...seleccion, idOpcion],
    );
  }

  return (
    <div className="campo combo" ref={ref}>
      {etiqueta ? <label htmlFor={id}>{etiqueta}</label> : null}
      <button
        type="button"
        id={id}
        className="combo-disparador"
        onClick={() => !deshabilitado && setAbierto((v) => !v)}
        aria-expanded={abierto}
        aria-haspopup="listbox"
        disabled={deshabilitado}
      >
        <span>{resumenSeleccion(seleccion, opciones, textoVacio)}</span>
        <span aria-hidden="true">▾</span>
      </button>

      {abierto ? (
        <PanelFlotante ancla={ref} className="combo-panel" role="listbox">
          {buscador ? <BuscadorDesplegable etiqueta={etiqueta ?? 'opciones'} valor={buscar} alCambiar={setBuscar} /> : null}
          {opciones.length === 0 ? <div className="combo-vacio">Sin opciones.</div> : null}
          {sinCoincidencias ? <div className="combo-vacio">Ninguna opción coincide con la búsqueda.</div> : null}
          {visibles.map((o) => (
            <div key={o.id} className="combo-opcion combo-opcion-casilla">
              <CasillaVerificacion marcada={seleccion.includes(o.id)} alCambiar={() => alternar(o.id)} etiqueta={o.nombre} />
              <span onClick={() => alternar(o.id)}>{o.nombre}</span>
            </div>
          ))}
        </PanelFlotante>
      ) : null}
    </div>
  );
}

/** Igual que `SelectorUbicacion`, pero de selección múltiple — mismo árbol expandible, con casillas. */
/** Igual que `FilaArbolUbicacion`, pero con casillas — para el selector múltiple. */
function FilaArbolUbicacionMultiple({
  nodo,
  hijosPorPadre,
  nivel,
  seleccion,
  expandidos,
  alternarExpandido,
  alternarSeleccion,
}: {
  nodo: NodoUbicacion;
  hijosPorPadre: Map<string, NodoUbicacion[]>;
  nivel: number;
  seleccion: string[];
  expandidos: Set<string>;
  alternarExpandido: (id: string) => void;
  alternarSeleccion: (id: string) => void;
}): JSX.Element {
  const hijos = hijosPorPadre.get(nodo.id) ?? [];
  const expandido = expandidos.has(nodo.id);
  return (
    <div>
      <div className="combo-fila-raiz" style={{ paddingLeft: 22 * (nivel - 1) }}>
        {hijos.length > 0 ? (
          <button
            type="button"
            className="combo-chevron"
            onClick={() => alternarExpandido(nodo.id)}
            aria-label={expandido ? `Contraer ${nodo.nombre}` : `Expandir ${nodo.nombre}`}
          >
            {expandido ? '▾' : '▸'}
          </button>
        ) : (
          <span className="combo-chevron-espacio" aria-hidden="true" />
        )}
        <label className={`combo-opcion combo-opcion-casilla${nivel === 1 ? ' combo-opcion-raiz' : ' combo-opcion-hijo'}`}>
          <input type="checkbox" checked={seleccion.includes(nodo.id)} onChange={() => alternarSeleccion(nodo.id)} />
          {nodo.nombre}
        </label>
      </div>
      {expandido
        ? hijos.map((hijo) => (
            <FilaArbolUbicacionMultiple
              key={hijo.id}
              nodo={hijo}
              hijosPorPadre={hijosPorPadre}
              nivel={nivel + 1}
              seleccion={seleccion}
              expandidos={expandidos}
              alternarExpandido={alternarExpandido}
              alternarSeleccion={alternarSeleccion}
            />
          ))
        : null}
    </div>
  );
}

export function SelectorUbicacionMultiple({
  id,
  etiqueta = 'Ubicación',
  seleccion,
  alCambiar,
  ubicaciones,
  textoVacio = 'Todas',
  buscador: buscadorForzado,
}: {
  id: string;
  etiqueta?: string;
  seleccion: string[];
  alCambiar: (valores: string[]) => void;
  ubicaciones: NodoUbicacion[] | undefined;
  textoVacio?: string;
  /** Con término, muestra las coincidencias en lista plana con su ruta completa, en vez del árbol. */
  buscador?: boolean;
}): JSX.Element {
  const [abierto, setAbierto] = useState(false);
  const [expandidos, setExpandidos] = useState<Set<string>>(new Set());
  const [buscar, setBuscar] = useState('');
  const ref = useCerrarAlExterior(abierto, () => setAbierto(false));

  const lista = ubicaciones ?? [];
  const raices = lista.filter((u) => !u.padreId);
  const hijosPorPadre = new Map<string, NodoUbicacion[]>();
  for (const u of lista) {
    if (u.padreId) hijosPorPadre.set(u.padreId, [...(hijosPorPadre.get(u.padreId) ?? []), u]);
  }
  const buscador = buscadorForzado ?? true;
  const coincidencias = buscador && buscar.trim() !== '' ? coincidenciasUbicacion(lista, buscar) : null;

  function alternarExpandido(idNodo: string): void {
    setExpandidos((prev) => {
      const copia = new Set(prev);
      if (copia.has(idNodo)) copia.delete(idNodo);
      else copia.add(idNodo);
      return copia;
    });
  }

  function alternarSeleccion(idNodo: string): void {
    alCambiar(seleccion.includes(idNodo) ? seleccion.filter((v) => v !== idNodo) : [...seleccion, idNodo]);
  }

  return (
    <div className="campo combo" ref={ref}>
      {etiqueta ? <label htmlFor={id}>{etiqueta}</label> : null}
      <button
        type="button"
        id={id}
        className="combo-disparador"
        onClick={() => setAbierto((v) => !v)}
        aria-expanded={abierto}
        aria-haspopup="listbox"
      >
        <span>{resumenSeleccion(seleccion, lista, textoVacio)}</span>
        <span aria-hidden="true">▾</span>
      </button>

      {abierto ? (
        <PanelFlotante ancla={ref} className="combo-panel" role="listbox">
          {buscador ? <BuscadorDesplegable etiqueta={etiqueta} valor={buscar} alCambiar={setBuscar} /> : null}
          {coincidencias ? (
            coincidencias.length === 0 ? (
              <div className="combo-vacio">Ninguna ubicación coincide con la búsqueda.</div>
            ) : (
              coincidencias.map(({ nodo, ruta }) => (
                <div key={nodo.id} className="combo-opcion combo-opcion-casilla">
                  <CasillaVerificacion marcada={seleccion.includes(nodo.id)} alCambiar={() => alternarSeleccion(nodo.id)} etiqueta={ruta} />
                  <span onClick={() => alternarSeleccion(nodo.id)}>{ruta}</span>
                </div>
              ))
            )
          ) : (
            <>
              {raices.length === 0 ? <div className="combo-vacio">No hay ubicaciones creadas.</div> : null}
              {raices.map((raiz) => (
                <FilaArbolUbicacionMultiple
                  key={raiz.id}
                  nodo={raiz}
                  hijosPorPadre={hijosPorPadre}
                  nivel={1}
                  seleccion={seleccion}
                  expandidos={expandidos}
                  alternarExpandido={alternarExpandido}
                  alternarSeleccion={alternarSeleccion}
                />
              ))}
            </>
          )}
        </PanelFlotante>
      ) : null}
    </div>
  );
}

const DIAS_SEMANA_CORTOS = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];

function claveFecha(fecha: Date): string {
  return `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}-${String(fecha.getDate()).padStart(2, '0')}`;
}

function diasDelMes(mesVisible: Date): Date[] {
  const primerDia = new Date(mesVisible.getFullYear(), mesVisible.getMonth(), 1);
  const offset = primerDia.getDay();
  const inicio = new Date(primerDia);
  inicio.setDate(inicio.getDate() - offset);
  return Array.from({ length: 42 }, (_, i) => {
    const dia = new Date(inicio);
    dia.setDate(dia.getDate() + i);
    return dia;
  });
}

/**
 * Selector de fecha propio: evita el picker nativo del sistema operativo, que
 * en móvil se abre como una hoja aparte y en un modal queda encimado o corta
 * el resto del formulario.
 */
export function SelectorFecha({
  id,
  etiqueta,
  valor,
  alCambiar,
  deshabilitado = false,
  minimo,
  maximo,
}: {
  id: string;
  etiqueta?: string;
  /** Fecha en formato `AAAA-MM-DD`, o cadena vacía si no hay selección. */
  valor: string;
  alCambiar: (valor: string) => void;
  deshabilitado?: boolean;
  /** Primer día elegible (`AAAA-MM-DD`); los anteriores aparecen deshabilitados. */
  minimo?: string;
  /** Último día elegible (`AAAA-MM-DD`); los posteriores aparecen deshabilitados. */
  maximo?: string;
}): JSX.Element {
  const [abierto, setAbierto] = useState(false);
  const ref = useCerrarAlExterior(abierto, () => setAbierto(false));

  const fechaValor = valor ? new Date(`${valor}T00:00:00`) : null;
  const [mesVisible, setMesVisible] = useState(() => {
    const base = fechaValor ?? new Date();
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });

  const dias = diasDelMes(mesVisible);
  const hoyClave = claveFecha(new Date());

  function elegir(dia: Date): void {
    alCambiar(claveFecha(dia));
    setAbierto(false);
  }

  return (
    <div className="campo combo" ref={ref}>
      {etiqueta ? <label htmlFor={id}>{etiqueta}</label> : null}
      <button
        type="button"
        id={id}
        className="combo-disparador"
        onClick={() => !deshabilitado && setAbierto((v) => !v)}
        aria-expanded={abierto}
        aria-haspopup="dialog"
        disabled={deshabilitado}
      >
        <span>{valor ? formatearFechaCorta(fechaValor!) : 'dd/mm/aaaa'}</span>
        <span aria-hidden="true">▾</span>
      </button>

      {abierto ? (
        <PanelFlotante ancla={ref} ancho={260} alturaMaxima={Infinity} className="combo-panel combo-panel-calendario" role="dialog">
          <div className="calendario-mini-cabecera">
            <button
              type="button"
              className="combo-chevron"
              onClick={() => setMesVisible((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1))}
              aria-label="Mes anterior"
            >
              ←
            </button>
            <span style={{ textTransform: 'capitalize' }}>
              {mesVisible.toLocaleDateString('es-PE', { month: 'long', year: 'numeric' })}
            </span>
            <button
              type="button"
              className="combo-chevron"
              onClick={() => setMesVisible((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1))}
              aria-label="Mes siguiente"
            >
              →
            </button>
          </div>

          <div className="calendario-mini-cuadricula">
            {DIAS_SEMANA_CORTOS.map((d, i) => (
              <div key={i} className="calendario-mini-dia-nombre">
                {d}
              </div>
            ))}
            {dias.map((dia) => {
              const clave = claveFecha(dia);
              const fueraDeMes = dia.getMonth() !== mesVisible.getMonth();
              const fueraDeRango = (minimo !== undefined && clave < minimo) || (maximo !== undefined && clave > maximo);
              return (
                <button
                  key={clave}
                  type="button"
                  disabled={fueraDeRango}
                  style={fueraDeRango ? { opacity: 0.3, cursor: 'not-allowed' } : undefined}
                  className={`calendario-mini-dia${fueraDeMes ? ' fuera-mes' : ''}${clave === valor ? ' seleccionado' : ''}${clave === hoyClave ? ' hoy' : ''}`}
                  onClick={() => elegir(dia)}
                >
                  {dia.getDate()}
                </button>
              );
            })}
          </div>

          <div className="calendario-mini-acciones">
            <button type="button" className="combo-opcion" onClick={() => elegir(new Date())}>
              Hoy
            </button>
            <button
              type="button"
              className="combo-opcion"
              onClick={() => {
                alCambiar('');
                setAbierto(false);
              }}
            >
              Borrar
            </button>
          </div>
        </PanelFlotante>
      ) : null}
    </div>
  );
}

/** Selector de mes («AAAA-MM») con navegación por año; vacío significa «todos los meses». */
export function SelectorMes({
  id,
  etiqueta,
  valor,
  alCambiar,
}: {
  id: string;
  etiqueta?: string;
  /** Mes en formato `AAAA-MM`, o cadena vacía para no filtrar por mes. */
  valor: string;
  alCambiar: (valor: string) => void;
}): JSX.Element {
  const [abierto, setAbierto] = useState(false);
  const ref = useCerrarAlExterior(abierto, () => setAbierto(false));
  const [anioVisible, setAnioVisible] = useState(() => new Date().getFullYear());

  const hoy = new Date();
  const claveHoy = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}`;
  const mesValor = valor ? new Date(`${valor}-01T00:00:00`) : null;

  function abrir(): void {
    // Al abrir, el panel muestra el año del mes elegido (o el actual si no hay ninguno).
    setAnioVisible((mesValor ?? hoy).getFullYear());
    setAbierto((v) => !v);
  }

  function elegir(clave: string): void {
    alCambiar(clave);
    setAbierto(false);
  }

  return (
    <div className="campo combo" ref={ref}>
      {etiqueta ? <label htmlFor={id}>{etiqueta}</label> : null}
      <button type="button" id={id} className="combo-disparador" onClick={abrir} aria-expanded={abierto} aria-haspopup="dialog">
        <span>
          {mesValor
            ? `${nombreDeMes(mesValor.getMonth())} ${mesValor.getFullYear()}`
            : 'Todos los meses'}
        </span>
        <span aria-hidden="true">▾</span>
      </button>

      {abierto ? (
        <PanelFlotante ancla={ref} ancho={260} alturaMaxima={Infinity} className="combo-panel combo-panel-calendario" role="dialog">
          <div className="calendario-mini-cabecera">
            <button type="button" className="combo-chevron" onClick={() => setAnioVisible((a) => a - 1)} aria-label="Año anterior">
              ←
            </button>
            <span>{anioVisible}</span>
            <button type="button" className="combo-chevron" onClick={() => setAnioVisible((a) => a + 1)} aria-label="Año siguiente">
              →
            </button>
          </div>

          <div className="calendario-mini-cuadricula" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
            {Array.from({ length: 12 }, (_, indice) => {
              const clave = `${anioVisible}-${String(indice + 1).padStart(2, '0')}`;
              return (
                <button
                  key={clave}
                  type="button"
                  className={`calendario-mini-dia${clave === valor ? ' seleccionado' : ''}${clave === claveHoy ? ' hoy' : ''}`}
                  onClick={() => elegir(clave)}
                >
                  {nombreDeMes(indice).slice(0, 3)}
                </button>
              );
            })}
          </div>

          <div className="calendario-mini-acciones">
            <button type="button" className="combo-opcion" onClick={() => elegir(claveHoy)}>
              Este mes
            </button>
            <button type="button" className="combo-opcion" onClick={() => elegir('')}>
              Todos
            </button>
          </div>
        </PanelFlotante>
      ) : null}
    </div>
  );
}

/** Nombre del mes en español con mayúscula inicial solamente (0 = enero). */
function nombreDeMes(indice: number): string {
  const nombre = new Date(2000, indice, 1).toLocaleDateString('es-PE', { month: 'long' });
  return nombre.charAt(0).toUpperCase() + nombre.slice(1);
}

function formatearFechaCorta(fecha: Date): string {
  return `${String(fecha.getDate()).padStart(2, '0')}/${String(fecha.getMonth() + 1).padStart(2, '0')}/${fecha.getFullYear()}`;
}

/** Selector de una sola opción con buscador dentro del desplegable, como el de catálogos. */
export function SelectorBuscable({
  id,
  etiqueta,
  valor,
  alCambiar,
  opciones,
  textoVacio = 'Seleccione…',
  vacio = 'Sin resultados.',
}: {
  id: string;
  etiqueta: string;
  valor: string;
  alCambiar: (valor: string) => void;
  opciones: Opcion[];
  textoVacio?: string;
  vacio?: string;
}): JSX.Element {
  const [abierto, setAbierto] = useState(false);
  const [buscar, setBuscar] = useState('');
  const ref = useCerrarAlExterior(abierto, () => setAbierto(false));

  const seleccionado = opciones.find((o) => o.id === valor);
  const termino = buscar.trim().toLowerCase();
  const coincidencias = opciones.filter((o) => o.nombre.toLowerCase().includes(termino));

  function elegir(opcion: Opcion): void {
    alCambiar(opcion.id);
    setBuscar('');
    setAbierto(false);
  }

  return (
    <div className="campo combo" ref={ref}>
      <label htmlFor={id}>{etiqueta}</label>
      <button
        type="button"
        id={id}
        className="combo-disparador"
        onClick={() => setAbierto((v) => !v)}
        aria-expanded={abierto}
        aria-haspopup="listbox"
      >
        <span>{seleccionado ? seleccionado.nombre : textoVacio}</span>
        <span aria-hidden="true">▾</span>
      </button>

      {abierto ? (
        <PanelFlotante ancla={ref} className="combo-panel" role="listbox">
          <div className="campo cabecera-lista-buscar" style={{ margin: 6 }}>
            <IconoLupa />
            <input
              aria-label={`Buscar ${etiqueta.toLowerCase()}`}
              placeholder="Buscar…"
              value={buscar}
              autoFocus
              onChange={(e) => setBuscar(e.target.value)}
            />
          </div>
          {coincidencias.map((o) => {
            const marcada = o.id === valor;
            return (
              <button
                key={o.id}
                type="button"
                className={`combo-opcion${marcada ? ' seleccionada' : ''}`}
                onClick={() => elegir(o)}
                style={{ display: 'flex', alignItems: 'center', gap: 10 }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: '50%',
                    border: '1.5px solid var(--texto-tenue)',
                    background: marcada ? 'var(--acento)' : 'transparent',
                    flexShrink: 0,
                  }}
                />
                {o.nombre}
              </button>
            );
          })}
          {coincidencias.length === 0 ? <div className="combo-vacio">{vacio}</div> : null}
        </PanelFlotante>
      ) : null}
    </div>
  );
}
